// SignIn.jsx
import React, { useEffect, useState } from "react";
import { Avatar, Button, CssBaseline, Box, Typography, Container, CardContent, FormControlLabel, Checkbox, Backdrop, CircularProgress, Link, Divider } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import PersonalInformation from "./PersonalInformationForm";
import { countrylist } from "../../Model/country";
import { CityData } from "../../Model/city";
import { registerUser, checkEmail_and_Phone_Exists } from "./api";
import { useLocation, useNavigate } from "react-router-dom";

const defaultTheme = createTheme();

export default function SignIn() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [personalInfoLoading, setPersonalInfoLoading] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [countryPick, setCountryPick] = useState("");
  const [cityPick, setCityPick] = useState("");
  const [messageSuccess, setMessageSuccess] = useState("");
  const [messageStatus, setMessageStatus] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [agree, setAgree] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const { state } = useLocation();
  const { userInfo } = state || {};
  const [isSubmit, setIsSubmit] = useState(false);
  const [backpage, setBack] = useState(false);
  const [headTitle, setHeadTitle] = useState("Register Your Account");

  const [form, setForm] = useState({
    firstname: "",
    lastname: "",
    phone: "",
    dob: "",
    address: "",
    second_address_line: "",
    postal_code: "",
    email: "",
    password: "",
    nationality: "",
    confirm_password: "",
  });

  const [selectedCountry, setSelectedCountry] = React.useState(null);

  // Load countries initial setup
  useEffect(() => {
    if (countrylist?.countries) {
      setCountryData(countrylist.countries);
    }
  }, []);

  // Isakay ang userInfo kung may galing sa OAuth/External state
  useEffect(() => {
    if (userInfo) {
      setForm((prev) => ({
        ...prev,
        ...userInfo,
      }));
      if (userInfo.country) {
        setCountryPick(userInfo.country);
        setCityList(CityData[userInfo.country] || []);
        const foundCountry = countrylist?.countries?.find(c => c.name === userInfo.country);
        if (foundCountry) setSelectedCountry(foundCountry);
      }
      if (userInfo.city) {
        setCityPick(userInfo.city);
      }
      setStep(2);
      setHeadTitle("Complete Your Registration");
      setBack(true);
    }
  }, [userInfo]);

  // ✅ SOLUSYON PARA SA BACK: Siguraduhing may kargang cityList palagi kapag may napiling countryPick
  useEffect(() => {
    if (countryPick && CityData[countryPick]) {
      setCityList(CityData[countryPick]);
    } else {
      setCityList([]);
    }
  }, [countryPick]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: false }));
  };

  const handleCountrySelect = (newValue) => {
    if (!newValue) {
      setSelectedCountry(null);
      setCityList([]);
      setCountryPick("");
      setCityPick(""); // Linisin ang siyudad kapag tinanggal ang bansa
      handleChange({ target: { name: "country", value: "" } });
      handleChange({ target: { name: "city", value: "" } });
      return;
    }

    setSelectedCountry(newValue);
    setCountryPick(newValue.name);
    setCityList(CityData[newValue.name] || []);
    setCityPick(""); // Reset city pag nagbago ng bansa para iwas maling data

    handleChange({ target: { name: "country", value: newValue.name } });
    handleChange({ target: { name: "city", value: "" } });
  };


  const handleCitySelect = (e, v) => {
    const value = v || "";
    setCityPick(value);
    // I-update ang master form state para hindi mawala ang value nito
    setForm((prev) => ({ ...prev, city: value }));
    setErrors((prev) => ({ ...prev, city: false }));
  };
  
  //   setCityPick(value);
  //   handleChange({ target: { name: "city", value: value } });
  // };

  const handleAgree = () => setAgree(!agree);

  const validatePersonalInfo = () => {
    const newErrors = {
      firstname: !form.firstname,
      lastname: !form.lastname,
      dob: !form.dob,
      address: !form.address,
      postal_code: !form.postal_code,
      email: !form.email,
      phone: !form.phone,
      nationality: !form.nationality,
      country: !countryPick,
      city: !cityPick,
      password: !form.password,
      confirm_password: form.confirm_password !== form.password,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handlePersonalInfoNext = async () => {
    if (!form.password) {
      setErrors((prev) => ({
        ...prev,
        password: "Use at least 6 characters, one uppercase, one lowercase, and one number.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, password: "" }));
    }

    if (form.password !== form.confirm_password) {
      setErrors((prev) => ({
        ...prev,
        confirm_password: "Passwords do not match.",
      }));
    } else {
      setErrors((prev) => ({ ...prev, confirm_password: "" }));
    }

    if (!form.password || form.password !== form.confirm_password) {
      return;
    }

    if (!validatePersonalInfo()) {
      alert("Please fill all required personal information correctly.");
      return;
    }

    setPersonalInfoLoading(true);

    try {
      const response = await checkEmail_and_Phone_Exists({
        email: form.email,
        phone: form.phone,
      });

      if (response?.S === false) {
        setMessageSuccess(response.msg || "Email or phone number already exists.");
        setMessageType("error");
        setMessageStatus(true);
      } else {
        setMessageSuccess(response.msg);
        setMessageType("success");
        setMessageStatus(true);
        setStep(2);
      }

      setTimeout(() => {
        setMessageStatus(false);
        setMessageSuccess("");
        setMessageType("");
      }, 5000);
    } catch (error) {
      setMessageSuccess("An error occurred while checking your information.");
      setMessageType("error");
      setMessageStatus(true);

      setTimeout(() => {
        setMessageStatus(false);
        setMessageSuccess("");
        setMessageType("");
      }, 5000);
    } finally {
      setPersonalInfoLoading(false);
    }
  };

  const handleBackHistory = () => setStep(1);
  const handleBackpage = () => navigate(-1);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (isNaN(date)) return dateStr;
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const handleCreateAccountSubmit = async (e) => {
    e.preventDefault();

    if (step !== 2) {
      alert("Please complete all steps first.");
      return;
    }

    if (!agree) {
      alert("You must agree to the Terms and Conditions.");
      return;
    }

    setLoading(true);
    setMessageSuccess("");
    setMessageStatus(false);

    try {
      let finalPhone = form.phone.trim();
      
      if (countryPick === "Philippines") {
        let clean = finalPhone.replace(/[^\d]/g, ""); 
        if (clean.startsWith("639")) {
          finalPhone = "0" + clean.slice(2);
        } 
        else if (clean.startsWith("9") && clean.length === 10) {
          finalPhone = "0" + clean;
        }
        else if (finalPhone.startsWith("+639")) {
          finalPhone = "0" + finalPhone.slice(3).replace(/[^\d]/g, "");
        }
      }

      const pureUserData = {
        firstname: form.firstname,
        lastname: form.lastname,
        dob: form.dob,
        address: form.address,
        second_address_line: form.second_address_line,
        postal_code: form.postal_code,
        city: cityPick,
        country: countryPick,
        email: form.email,
        password: form.password,
        phone: finalPhone,
        nationality: form.nationality,
      };

      console.log('Submitting user registration data: ', pureUserData);
      const results = await registerUser(pureUserData);

      if (results?.success === false) {
        setMessageType("error");
        setMessageSuccess(results.msg || "Failed to create account.");
        setMessageStatus(true);
        return;
      }

      setMessageType("success");
      setMessageSuccess(results.msg);
      setIsSubmit(true);
      setMessageStatus(true);
    } catch (error) {
      console.error("❌ Error:", error);
      setMessageType("error");
      setMessageSuccess(error.message || "Something went wrong.");
      setMessageStatus(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Container
        component="main"
        maxWidth="md"
        sx={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <CssBaseline />

        <Box
          sx={{
            mt: { xs: 6, sm: 10 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: { xs: 3, sm: 4 },
              maxWidth: 500,
              lineHeight: 1.6,
              px: { xs: 1, sm: 0 },
            }}
          >
            Sign up to receive Leon Gallery’s weekly art news update and
            personalized auction updates. You can unsubscribe at any time.
          </Typography>

          <Avatar sx={{ m: 1, bgcolor: "#2c3136" }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography
            component="h1"
            variant="h5"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            {headTitle}
          </Typography>

          {messageStatus && (
            <Box
              sx={{
                width: "100%",
                mt: 3,
                mb: -2,
                px: { xs: 1.5, sm: 2 },
                py: 1.5,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                backgroundColor:
                  messageType === "error" ? "#fdecea" : "#edf7ed",
                color: messageType === "error" ? "#d32f2f" : "#2e7d32",
                border: "1px solid",
                borderColor: messageType === "error" ? "#f44336" : "#4caf50",
              }}
            >
              {messageType === "error" ? (
                <CloseIcon sx={{ mr: 1 }} />
              ) : (
                <CheckCircleIcon sx={{ mr: 1 }} />
              )}
              <Typography variant="body2">{messageSuccess}</Typography>
            </Box>
          )}

          <Box
            component="form"
            noValidate
            onSubmit={handleCreateAccountSubmit}
            sx={{
              mt: { xs: 3, sm: 4 },
              width: "100%",
              px: { xs: 0.5, sm: 0 },
            }}
          >
            
            {step === 1 && (
              <CardContent>
                <PersonalInformation
                  form={form}
                  selectedCountry={selectedCountry}
                  setSelectedCountry={setSelectedCountry}
                  handleCountrySelect={handleCountrySelect}
                  handleChange={handleChange}
                  countryData={countrylist.countries}
                  cityList={cityList}
                  userInfo={userInfo}
                  handleCitySelect={handleCitySelect}
                  errors={errors}
                  setErrors={setErrors}
                  disabled={false}
                  cityPick={cityPick} // Ipinasa natin para magamit sa component value
                />
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    mt: 2,
                    backgroundColor: "#343a40",
                    "&:hover": { backgroundColor: "#2c3136" },
                  }}
                  onClick={handlePersonalInfoNext}
                  disabled={personalInfoLoading}
                >
                  {personalInfoLoading ? "Checking..." : "Next"}
                </Button>
              </CardContent>
            )}

            
            {step === 2 && (
              <CardContent>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Review Your Information
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    mb: 3,
                    backgroundColor: "#f9f9f9",
                    p: { xs: 2, sm: 3 },
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                >
                  <Box sx={{ textAlign: "left" }}>
                    <Typography variant="h6" color="primary" gutterBottom>
                      Personal Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    {[
                      ["First Name", form.firstname],
                      ["Last Name", form.lastname],
                      ["Date of Birth", formatDate(form.dob)],
                      ["Address", form.address],
                      ["Second Address Line", form.second_address_line],
                      ["Postal Code", form.postal_code],
                      ["City", cityPick],
                      ["Country", countryPick],
                      ["Email", form.email],
                      ["Phone", form.phone],
                      ["Nationality", form.nationality],
                    ].map(([label, value]) => (
                      <Typography key={label} sx={{ mb: 1 }}>
                        <strong>{label}:</strong> {value || "-"}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                <FormControlLabel
                  sx={{ mt: 1, display: "flex", justifySelf: "flex-start" }}
                  control={
                    <Checkbox
                      checked={agree}
                      onChange={handleAgree}
                      disabled={isSubmit}
                      color="primary"
                      size="medium"
                    />
                  }
                  label={
                    <Typography variant="body2" color="textSecondary">
                      I agree to the{" "}
                      <Link
                        href="/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                      >
                        Terms and Conditions
                      </Link>
                      .
                    </Typography>
                  }
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 4,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={backpage === true ? handleBackpage : handleBackHistory}
                    disabled={isSubmit}
                    sx={{
                      color: "black",
                      borderColor: "black",
                      "&:hover": {
                        backgroundColor: "#2c3136",
                        color: "white",
                        borderColor: "black",
                      },
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading || !agree || isSubmit}
                    variant="contained"
                    sx={{
                      backgroundColor: "#343a40",
                      "&:hover": { backgroundColor: "#2c3136" },
                    }}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </Box>
              </CardContent>
            )}
          </Box>
        </Box>

        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backdropFilter: "blur(3px)",
          }}
          open={loading || personalInfoLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    </ThemeProvider>
  );
}